// Glassmorphic Notification Toast System - Slides up from LEFT side of workspace
import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Database } from '@/database/Database';
import type { NotificationData } from '@/entities/Notification';
import { GlassNotificationToast } from './GlassNotificationToast';

interface NotificationPanelProps {
  userId: string;
}

export function NotificationPanel({ userId }: NotificationPanelProps) {
  const [notifications, setNotifications] = useState<(NotificationData & { id: number })[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [visibleNotifications, setVisibleNotifications] = useState<(NotificationData & { id: number })[]>([]);

  const loadNotifications = async () => {
    try {
      if (!userId) {
        console.log('⚠️ NotificationPanel: No userId provided');
        return;
      }

      console.log('📥 Loading notifications for userId:', userId);
      const db = new Database();
      const result = await db.query(
        `SELECT id, data, created_at FROM entities 
         WHERE entity_type = 'Notification' 
         ORDER BY created_at DESC`
      );

      if (!result) {
        console.error('❌ Database query returned null');
        return;
      }

      // Turso returns rows directly as an array
      const rows = Array.isArray(result) ? result : [];
      console.log(`📊 Found ${rows.length} total notifications in database`);

      const allNotifs = rows
        .map((row: any) => {
          try {
            const data = JSON.parse(row.data);
            return { ...data, id: row.id };
          } catch (e) {
            console.error('❌ Failed to parse notification:', e);
            return null;
          }
        })
        .filter((n): n is NotificationData & { id: number } => {
          // Filter: notification must be for this user AND not sent by this user (don't show your own broadcasts)
          // sentBy might be undefined for old notifications - that's OK, show those
          return n !== null && n.userId === String(userId) && (!n.sentBy || n.sentBy !== String(userId));
        });

      console.log(`✅ Found ${allNotifs.length} notifications for this user`);
      setNotifications(allNotifs);
      
      // Show only unread/unacknowledged notifications as toasts
      const unacknowledged = allNotifs.filter(n => !n.acknowledged);
      console.log(`🔔 ${unacknowledged.length} unacknowledged notifications will be shown as toasts`);
      setVisibleNotifications(unacknowledged.slice(0, 3)); // Max 3 visible at once
      setUnreadCount(allNotifs.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const dismissNotification = async (notifId: number) => {
    console.log('🗑️ Dismiss clicked for notification ID:', notifId);
    try {
      const db = new Database();
      const notif = notifications.find(n => n.id === notifId);
      if (!notif) {
        console.log('❌ Notification not found in state');
        return;
      }

      const updatedNotif = { ...notif, read: true, acknowledged: true };
      delete (updatedNotif as any).id;

      console.log('💾 Updating notification in database...');
      await db.query(
        `UPDATE entities SET data = ?, updated_at = ? WHERE id = ?`,
        [JSON.stringify(updatedNotif), new Date().toISOString(), notifId]
      );

      console.log('✅ Database updated, removing from UI...');
      // Remove from visible toasts immediately
      setVisibleNotifications(prev => prev.filter(n => n.id !== notifId));
      setNotifications(prev =>
        prev.map(n => n.id === notifId ? { ...n, read: true, acknowledged: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      console.log('✅ Notification dismissed successfully');
    } catch (error) {
      console.error('❌ Failed to dismiss notification:', error);
    }
  };

  const dismissAll = async () => {
    console.log('🗑️ Dismissing all notifications...');
    try {
      const db = new Database();
      
      // Get ALL notifications from database (not just visible ones)
      const result = await db.query(
        `SELECT id, data FROM entities WHERE entity_type = 'Notification'`
      );
      
      console.log(`Found ${result.length} total notifications in database`);
      
      for (const row of result) {
        const notif = JSON.parse(row.data);
        notif.acknowledged = true;
        notif.dismissed = true;
        delete (notif as any).id;
        
        await db.query(
          `UPDATE entities SET data = ?, updated_at = ? WHERE id = ?`,
          [JSON.stringify(notif), new Date().toISOString(), row.id]
        );
      }
      
      setVisibleNotifications([]);
      setNotifications([]);
      setUnreadCount(0);
      console.log('✅ All notifications dismissed from database');
    } catch (error) {
      console.error('❌ Failed to dismiss all notifications:', error);
    }
  };

  useEffect(() => {
    if (!userId) return;
    loadNotifications();
    // Poll every 3 seconds for new notifications (more responsive)
    const interval = setInterval(loadNotifications, 3000);
    return () => clearInterval(interval);
  }, [userId]);

  return (
    <>
      {/* Notification Toasts - iPhone-style: RIGHT side, stack from TOP */}
      <div 
        className="fixed right-6 top-20 z-[9998] flex flex-col gap-3 max-h-[calc(100vh-6rem)] overflow-visible pointer-events-none"
      >
        {visibleNotifications.map((notif, index) => (
          <div key={notif.id} className="pointer-events-auto">
            <GlassNotificationToast
              notification={notif}
              onDismiss={dismissNotification}
              index={index}
            />
          </div>
        ))}
        
        {/* Clear All Button - appears when there are notifications */}
        {visibleNotifications.length > 0 && (
          <button
            onClick={dismissAll}
            className="pointer-events-auto self-center mt-2 px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/50 text-red-200 text-sm font-semibold transition-colors backdrop-blur-xl"
          >
            Clear All ({visibleNotifications.length})
          </button>
        )}
      </div>

      {/* Small bell icon indicator - MOBILE ONLY */}
      {unreadCount > 0 && (
        <div className="md:hidden fixed top-4 right-4 z-[9999]">
          <div className="relative">
            <Bell className="w-5 h-5 text-purple-400" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs font-bold flex items-center justify-center">
              {unreadCount}
            </span>
          </div>
        </div>
      )}
    </>
  );
}
